'use client'

import { useMemo } from 'react'
import { IconArrowRight } from '@tabler/icons-react'

import Button from 'shared/ui/Button'
import { removeEmptySpacesFromCurrency } from 'shared/utils/currency'
import { addTestId } from 'shared/utils/tests'

import { CartPorts } from '../../../ports'
import {
  generateSummaryButtonTestTag,
  getSummaryButtonId,
  getSummaryButtonLabelByPage
} from '../utils'

import { CartSummaryDisabledButton } from './CartSummaryDisabledButton'

import styles from './CartSummaryButton.module.scss'

const CartSummaryButton = ({
  pageType,
  isPaymentPage,
  disableConditionErrors,
  isCartDrawer,
  isMobile,
  redirect,
  authorize,
  navigate,
  isLoading,
  paymentValue,
  ports
}: {
  pageType: string
  isPaymentPage: boolean
  disableConditionErrors: string[]
  isCartDrawer: boolean
  isMobile: boolean
  redirect: () => void
  authorize?: () => void
  navigate: () => void
  isLoading: boolean
  paymentValue: string
  ports: CartPorts
}) => {
  const { PaymentAction } = ports
  const isCartDrawerOnPaymentPage = isCartDrawer && isPaymentPage

  const buttonTestTag = useMemo(
    () => generateSummaryButtonTestTag(isMobile, pageType, isCartDrawer),
    [isMobile, pageType, isCartDrawer]
  )

  if (isPaymentPage && !isCartDrawer) {
    return PaymentAction ? (
      <PaymentAction
        cartValidationErrors={disableConditionErrors}
        paymentValue={paymentValue}
        testTag={buttonTestTag}
      />
    ) : null
  }

  if (disableConditionErrors.length > 0 && !isCartDrawerOnPaymentPage) {
    return (
      <CartSummaryDisabledButton
        errors={disableConditionErrors}
        id={getSummaryButtonId(pageType, isCartDrawer)}
        loading={isLoading}
        testTag={buttonTestTag}
        endIcon={isCartDrawer ? <IconArrowRight /> : undefined}
      >
        {isCartDrawer ? (
          <>Do kasy {removeEmptySpacesFromCurrency(paymentValue)}</>
        ) : (
          <>
            {getSummaryButtonLabelByPage(pageType, paymentValue)}
            <IconArrowRight />
          </>
        )}
      </CartSummaryDisabledButton>
    )
  }

  if (isCartDrawer) {
    return (
      <Button
        className={styles.proceedButton}
        onClick={redirect}
        id='to_cart_button'
        variant='primary'
        size='large'
        fullWidth
        loading={isLoading}
        disabled={isLoading}
        endIcon={isLoading ? <IconArrowRight /> : undefined}
        {...addTestId(buttonTestTag)}
      >
        Do kasy {removeEmptySpacesFromCurrency(paymentValue)}
      </Button>
    )
  }

  const handleNavigation = () => {
    if (authorize) {
      authorize()
    } else {
      navigate()
    }
  }

  return (
    <Button
      className={styles.proceedButton}
      id='cart_summary_proceed_button'
      variant='primary'
      onClick={handleNavigation}
      size='large'
      fullWidth
      loading={isLoading}
      disabled={isLoading}
      endIcon={isLoading ? <IconArrowRight /> : undefined}
      {...addTestId(buttonTestTag)}
    >
      {getSummaryButtonLabelByPage(pageType, paymentValue)}{' '}
    </Button>
  )
}

export default CartSummaryButton
